import { AddAwsButton } from './AddAwsButton'
import { AddFacebookButton } from './AddFacebookButton'
import { AddGoogleCalendarButton } from './AddGoogleCalendarButton'
import { AddGoogleDriveButton } from './AddGoogleDriveButton'
import { AddHttpButton } from './AddHttpButton'
import { AddJiraButton } from './AddJiraButton'
import { AddPostgresqlButton } from './AddPostgresqlButton'
import { Button, Menu, OverlayContent, Toggle } from '../../../components'
import { EllipsisIcon, PlusIcon, Trash2 } from 'lucide-react'
import { GetConnectionsListResultData } from '@indigohive/cogfy-types/endpoints/getConnectionsList'
import { MAP_LOGO_BY_PROVIDER } from '../../../components/__common/map-logo-by-provider'
import { Provider } from '@indigohive/cogfy-providers'
import { useCogfy, usePermissions } from '../../../hooks'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UUID } from '@indigohive/cogfy-types'
import clsx from 'clsx'
import { AddHerokuButton } from './AddHerokuButton'
import { AddGithubButton } from './AddGithubButton'
import { AddMetaMarketingApiButton } from './AddMetaMarketingApiButton'
import { AddRdStationButton } from './AddRdStationButton'
import { AddAzureAdButton } from './AddAzureAdButton'

export type ConnectionSectionProps = {
  onConnectClick?: (provider: Provider) => void
  onDeleteClick?: (connectionId: UUID) => void
  provider: Provider
  connections: GetConnectionsListResultData[]
  onRefetch?: () => void
  disableDelete?: boolean
}

export function ConnectionSection (props: ConnectionSectionProps) {
  const { provider, connections, onRefetch, onDeleteClick, disableDelete } = props
  const { t } = useTranslation()
  const cogfy = useCogfy()
  const permissions = usePermissions()
  const [openConnection, setOpenConnection] = useState<UUID | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [connectionToEdit, setConnectionToEdit] = useState<GetConnectionsListResultData | null>(null)
  const buttonText = t('WorkspaceConnectionsPage:Connect')

  const isAdmin = permissions.isAdmin
  const hasConnections = connections.length > 0
  const updateDefault = useMutation({
    mutationFn: async ({ _default }: { _default: boolean }) => {
      if (!connectionToEdit) return
      const connectionId = connectionToEdit.id
      await cogfy.updateConnectionDefault({ connectionId, default: _default })
      setAnchorEl(null)
      setConnectionToEdit(null)
      props.onRefetch?.()
    }
  })
  const updateEnabled = useMutation({
    mutationFn: async ({ connectionId, enabled }: { connectionId: UUID, enabled: boolean }) => {
      await cogfy.updateConnectionEnabled({ connectionId, enabled })
      props.onRefetch?.()
    }
  })

  return (
    <div className="w-full p-6 srounded-2xl bg-background-default">
      <div className="flex items-center px-2 mb-4">
        {MAP_LOGO_BY_PROVIDER[provider?.name] && (
          <img
            src={MAP_LOGO_BY_PROVIDER[provider?.name]}
            alt={`${provider?.label}-logo`}
            className="size-9 rounded-md"
          />
        )}
        <div className="grow flex flex-col gap-0.5 pl-4">
          <h3 className="font-semibold">
            {provider?.label}
          </h3>
          {provider.website && (
            <a
              className="link link-hover opacity-40 text-xs"
              target="_blank"
              rel="noreferrer"
              href={provider.website}
            >
              {provider.website}
            </a>
          )}
        </div>
        {isAdmin
          ? (
            <>
              {provider?.name === 'aws' && (<AddAwsButton onSuccess={onRefetch} color='primary'>{buttonText}</AddAwsButton>)}
              {provider?.name === 'azureAd' && (<AddAzureAdButton onSuccess={onRefetch} color='primary'>{buttonText}</AddAzureAdButton>)}
              {provider?.name === 'github' && (<AddGithubButton onSuccess={onRefetch} color='primary'>{buttonText}</AddGithubButton>)}
              {provider?.name === 'googleCalendar' && (<AddGoogleCalendarButton color='primary'>{buttonText}</AddGoogleCalendarButton>)}
              {provider?.name === 'googleDrive' && (<AddGoogleDriveButton color='primary'>{buttonText}</AddGoogleDriveButton>)}
              {provider?.name === 'heroku' && (<AddHerokuButton onSuccess={onRefetch} color='primary'>{buttonText}</AddHerokuButton>)}
              {provider?.name === 'http' && (<AddHttpButton onSuccess={onRefetch} color='primary'>{buttonText}</AddHttpButton>)}
              {provider?.name === 'jira' && (<AddJiraButton onSuccess={onRefetch} color='primary'>{buttonText}</AddJiraButton>)}
              {provider?.name === 'metaMarketingApi' && (<AddMetaMarketingApiButton onSuccess={onRefetch} color='primary'>{buttonText}</AddMetaMarketingApiButton>)}
              {provider?.name === 'postgresql' && (<AddPostgresqlButton onSuccess={onRefetch} color='primary'>{buttonText}</AddPostgresqlButton>)}
              {provider?.name === 'rdStation' && (<AddRdStationButton onSuccess={onRefetch} color='primary'>{buttonText}</AddRdStationButton>)}
              {provider?.name === 'whatsapp' && (<AddFacebookButton onSuccess={onRefetch} color='primary'>{buttonText}</AddFacebookButton>)}
              {props.onConnectClick && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => props.onConnectClick?.(provider)}
                >
                  <PlusIcon size={16} />
                  {buttonText}
                </Button>
              )}
            </>
            )
          : (
            <Button
              color="primary"
              size="sm"
              disabled={true}
            >
              <PlusIcon size={16} />
              {buttonText}
            </Button>
            )}
      </div>
      {!hasConnections && (
        <div className="text-sm px-2">
          {t('WorkspaceConnectionsPage:No connections set')}
        </div>
      )}
      {hasConnections && (
        <div className="flex flex-col gap-2">
          {connections.map(connection => (
            <div
              key={connection.id}
              tabIndex={0}
              className={clsx(
                'card',
                'card-border',
                'collapse',
                'bg-base-100',
                'border border-border-default',
                openConnection === connection.id ? 'collapse-open' : 'collapse-close h-16'
              )}
            >
              <div
                className="font-medium flex items-center collapse-title cursor-pointer pr-4"
                onClick={() => setOpenConnection(openConnection === connection.id ? null : connection.id)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <Toggle
                      color="primary"
                      checked={connection.enabled}
                      disabled={updateEnabled.isPending}
                      onChange={event => {
                        updateEnabled.mutate({ connectionId: connection.id, enabled: event.target.checked })
                      }}
                      onClick={event => event.stopPropagation()}
                    />
                    <div className={clsx('grow', !connection.enabled && 'opacity-40')}>
                      {connection.name}
                      &nbsp;
                      {connection.default && (
                        <span className="text-xs opacity-40">
                          ({t('Default')})
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className='flex flex-row gap-1'>
                      <Button
                        ghost
                        square
                        size="sm"
                        onClick={event => {
                          event.stopPropagation()
                          setAnchorEl(event.currentTarget)
                          setConnectionToEdit(connection)
                        }}
                      >
                        <EllipsisIcon size={20} />
                      </Button>
                      <Button
                        ghost
                        square
                        size='sm'
                        className='text-red-500'
                        onClick={event => {
                          event.stopPropagation()
                          onDeleteClick?.(connection.id)
                        }}
                        disabled={disableDelete}
                      >
                        <Trash2 size={20} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="collapse-content">
                <ul>
                  {connection.values.map(value => (
                    <li key={value.id} className="flex items-center">
                      <span>{value.name}: </span>
                      <span>{value.plainTextValue ?? '********'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
      <OverlayContent
        open={Boolean(connectionToEdit)}
        onClose={() => {
          setAnchorEl(null)
          setConnectionToEdit(null)
        }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Menu className={clsx(connectionToEdit?.default && '[&_li>*:not(ul):not(.menu-title):not(details):active]:bg-inherit')}>
          <li className={clsx(connectionToEdit?.default && 'menu-disabled')}>
            <a
              className="font-bold"
              onClick={() => updateDefault.mutate({ _default: true })}
            >
              {t('WorkspaceConnectionsPage:Set as default')}
            </a>
          </li>
        </Menu>
      </OverlayContent>
    </div>
  )
}
